// PDF version of the PDP audit report.
// Styling mirrors /Users/dev/Downloads/pdp-audit-report.jsx — dark
// background, gold (#b8966a) accents, real Oswald (display) + Lato (body)
// fonts bundled locally from src/assets/fonts.
//
// 4 pages: Overview · Human Conversion · Agentic Commerce · Projected.
//
// Each font weight is registered as its own family name (e.g. "Oswald-Bold")
// to avoid react-pdf's flaky weight resolver in the `fonts` array form.

import React from "react";
import {
    Document,
    Image,
    Link,
    Page,
    StyleSheet,
    Svg,
    Polygon,
    Line as SvgLine,
    Circle,
    Text as SvgText,
    Text,
    View,
} from "@react-pdf/renderer";
import {
    AGENTIC_COMMERCE_DIMENSION_LABELS,
    HUMAN_CONVERSION_SECTION_LABELS,
    PdpAudit,
    PdpAuditResponse,
} from "./types";
import {
    parseScore,
    projectAgenticCommerce,
    projectHumanConversion,
    projectOverall,
} from "./projection";
import { registerPdfFonts } from "../../utils";
// Logo lives in /public so it's served at the site root rather than bundled.
// @react-pdf/renderer fetches images by URL during PDF generation, so we
// build an absolute URL from window.location.origin.
const tdcLogoWhite =
    typeof window !== "undefined"
        ? `${window.location.origin}/logo-transparent-tdc-only-white.png`
        : "/logo-transparent-tdc-only-white.png";

registerPdfFonts();

// --- palette ------------------------------------------------------------
export const GOLD = "#b8966a";
export const GOLD_LIGHT = "#d4b896";
// Opaque choco-tone equivalents of the original rgba(184,150,106,*) on black.
// react-pdf's renderer was painting the rgba() strings as a teal/green fallback,
// so we precompose against the black background.
const GOLD_BORDER_STRONG = "#5c4a32";
const GOLD_BORDER_LIGHT = "#3d3120";
export const GOLD_BORDER_FAINT = "#241d13";
const GOLD_BORDER_HAIR = "#1c1610";
const ROW_ALT_BG = "#0d0a07";
export const PANEL_BG = "#13100b";
export const BLACK = "#000000";
export const WHITE = "#FFFFFF";
export const GRAY = "#999999";
export const GRAY_LIGHT = "#cccccc";

export const FONT_OSWALD = "Oswald";
export const FONT_OSWALD_MEDIUM = "Oswald-Medium";
export const FONT_OSWALD_BOLD = "Oswald-Bold";
export const FONT_LATO = "Lato";
export const FONT_LATO_BOLD = "Lato-Bold";

const FOOTER_TAGLINE =
    "Better product education. More confident shoppers. Stronger PDP performance.";
const FOOTER_PROOF = "See how brands are driving an average 4.4x sales lift";
const FOOTER_CTA_LABEL = "Book a 15-Minute Discovery Call";
const FOOTER_CTA_HREF = "https://thedesirecompany.com/contact";

const styles = StyleSheet.create({
    page: {
        flexDirection: "column",
        backgroundColor: BLACK,
        color: WHITE,
        paddingTop: 36,
        paddingHorizontal: 44,
        paddingBottom: 70,
        fontFamily: FONT_LATO,
        fontSize: 10,
    },
    logoWrap: {
        marginBottom: 16,
    },
    logo: {
        height: 28,
        width: 160,
        objectFit: "contain",
    },
    headerRule: {
        height: 3,
        backgroundColor: GOLD,
        marginBottom: 30,
    },
    h1Wrap: {
        flexDirection: "row",
        alignItems: "flex-end",
        marginBottom: 4,
    },
    h1Display: {
        fontFamily: FONT_OSWALD_BOLD,
        fontSize: 38,
        color: WHITE,
        letterSpacing: 4,
        textTransform: "uppercase",
        lineHeight: 1,
    },
    h1Accent: {
        fontFamily: FONT_OSWALD_MEDIUM,
        fontSize: 36,
        color: GOLD,
        marginLeft: 8,
        textTransform: "uppercase",
        lineHeight: 1,
    },
    heroPanel: {
        flexDirection: "row",
        alignItems: "center",
        gap: 22,
        marginVertical: 26,
        padding: 20,
        backgroundColor: PANEL_BG,
        borderWidth: 1,
        borderColor: GOLD_BORDER_FAINT,
    },
    heroThumb: {
        width: 130,
        height: 96,
        backgroundColor: "#161616",
        borderWidth: 1,
        borderColor: GOLD_BORDER_FAINT,
        alignItems: "center",
        justifyContent: "center",
        padding: 14,
    },
    heroThumbLogo: {
        width: "100%",
        height: "100%",
        objectFit: "contain",
    },
    heroBrand: {
        fontFamily: FONT_LATO_BOLD,
        fontSize: 18,
        color: WHITE,
    },
    heroProduct: {
        fontFamily: FONT_OSWALD,
        fontSize: 15,
        color: GOLD,
        marginTop: 2,
    },
    heroLabel: {
        fontFamily: FONT_LATO,
        fontSize: 10,
        color: GRAY,
        letterSpacing: 1,
        marginTop: 6,
    },
    heroUrl: {
        fontFamily: FONT_LATO,
        fontSize: 8,
        color: GRAY,
        marginTop: 4,
    },
    overallBadge: {
        alignSelf: "flex-start",
        backgroundColor: GOLD,
        paddingVertical: 10,
        paddingHorizontal: 28,
        marginBottom: 30,
    },
    overallBadgeText: {
        fontFamily: FONT_OSWALD_MEDIUM,
        fontSize: 14,
        color: BLACK,
        letterSpacing: 4,
        textTransform: "uppercase",
    },
    overallBadgePct: {
        fontFamily: FONT_OSWALD_BOLD,
        fontSize: 16,
        color: BLACK,
    },
    breakdownPanel: {
        borderWidth: 1,
        borderColor: GOLD_BORDER_LIGHT,
        padding: 24,
        marginBottom: 32,
    },
    breakdownTitle: {
        fontFamily: FONT_OSWALD_BOLD,
        fontSize: 12,
        color: WHITE,
        textAlign: "center",
        letterSpacing: 5,
        textTransform: "uppercase",
        marginBottom: 6,
    },
    breakdownRule: {
        width: 36,
        height: 2,
        backgroundColor: GOLD,
        alignSelf: "center",
        marginBottom: 22,
    },
    scoreRow: {
        flexDirection: "row",
        gap: 16,
    },
    scoreCol: {
        flex: 1,
        alignItems: "center",
    },
    scoreNumberPercent: {
        fontFamily: FONT_OSWALD_BOLD,
        fontSize: 46,
        color: GOLD,
        lineHeight: 1,
    },
    scoreNumber: {
        fontFamily: FONT_OSWALD_BOLD,
        fontSize: 40,
        color: GOLD,
        lineHeight: 1,
    },
    scoreSlash: {
        fontFamily: FONT_OSWALD,
        fontSize: 22,
        color: GRAY,
    },
    scoreLabel: {
        fontFamily: FONT_OSWALD,
        fontSize: 9,
        color: GRAY,
        letterSpacing: 2.5,
        marginTop: 10,
        textAlign: "center",
        textTransform: "uppercase",
    },
    sectionH2: {
        fontFamily: FONT_OSWALD_BOLD,
        fontSize: 12,
        color: WHITE,
        letterSpacing: 5,
        textTransform: "uppercase",
        marginBottom: 18,
    },
    findingsRow: {
        flexDirection: "row",
        gap: 16,
    },
    findingsCard: {
        flex: 1,
        borderWidth: 1,
        borderColor: GOLD_BORDER_STRONG,
        paddingTop: 22,
        paddingHorizontal: 22,
        paddingBottom: 16,
    },
    findingsTitle: {
        fontFamily: FONT_OSWALD_MEDIUM,
        fontSize: 18,
        color: GOLD,
        lineHeight: 1.2,
        marginBottom: 14,
    },
    bulletRow: {
        flexDirection: "row",
        marginBottom: 6,
    },
    bulletDot: {
        width: 10,
        color: GOLD,
        fontFamily: FONT_OSWALD,
    },
    bulletText: {
        flex: 1,
        fontFamily: FONT_LATO,
        fontSize: 10,
        color: GRAY_LIGHT,
        lineHeight: 1.7,
    },
    goldBanner: {
        backgroundColor: GOLD,
        paddingVertical: 16,
        paddingHorizontal: 28,
        marginBottom: 30,
    },
    goldBannerText: {
        fontFamily: FONT_OSWALD_BOLD,
        fontSize: 16,
        color: BLACK,
        letterSpacing: 2,
        textTransform: "uppercase",
        textAlign: "center",
    },
    radarWrap: {
        alignItems: "center",
        marginBottom: 28,
    },
    table: {
        marginTop: 6,
    },
    tableHeaderRow: {
        flexDirection: "row",
        backgroundColor: GOLD,
    },
    tableHeaderCell: {
        fontFamily: FONT_OSWALD_MEDIUM,
        fontSize: 10,
        color: BLACK,
        letterSpacing: 2,
        paddingVertical: 10,
        paddingHorizontal: 12,
        textTransform: "uppercase",
        textAlign: "center",
    },
    tableHeaderCellSmall: {
        fontFamily: FONT_OSWALD_MEDIUM,
        fontSize: 9,
        color: BLACK,
        letterSpacing: 1.2,
        paddingVertical: 10,
        paddingHorizontal: 5,
        textTransform: "uppercase",
        textAlign: "center",
    },
    tableRow: {
        flexDirection: "row",
        borderBottomWidth: 1,
        borderBottomColor: GOLD_BORDER_HAIR,
    },
    tableRowAlt: {
        flexDirection: "row",
        backgroundColor: ROW_ALT_BG,
        borderBottomWidth: 1,
        borderBottomColor: GOLD_BORDER_HAIR,
    },
    cellSection: {
        fontFamily: FONT_OSWALD,
        fontSize: 10,
        color: GOLD_LIGHT,
        paddingVertical: 11,
        paddingHorizontal: 12,
        lineHeight: 1.45,
        textAlign: "center",
    },
    cellSectionSmall: {
        fontFamily: FONT_OSWALD,
        fontSize: 9,
        color: GOLD_LIGHT,
        paddingVertical: 8,
        paddingHorizontal: 9,
        lineHeight: 1.45,
        textAlign: "center",
    },
    cellBody: {
        fontFamily: FONT_LATO,
        fontSize: 11,
        color: GRAY_LIGHT,
        paddingVertical: 11,
        paddingHorizontal: 12,
        lineHeight: 1.55,
        textAlign: "center",
    },
    cellBodySmall: {
        fontFamily: FONT_LATO,
        fontSize: 9.5,
        color: GRAY_LIGHT,
        paddingVertical: 8,
        paddingHorizontal: 9,
        lineHeight: 1.5,
        textAlign: "center",
    },
    cellLift: {
        fontFamily: FONT_LATO_BOLD,
        fontSize: 11,
        color: WHITE,
        paddingVertical: 11,
        paddingHorizontal: 12,
        textAlign: "center",
    },
    colSection: { width: "22%" },
    colScore: { width: "10%" },
    colStrengths: { width: "34%" },
    colGaps: { width: "34%" },
    projCategory: { width: "34%" },
    projCol: { width: "22%" },
    liftSection: { width: "26%" },
    liftScore: { width: "12%" },
    liftDriver: { width: "50%" },
    twoCol: {
        flexDirection: "row",
        gap: 24,
        marginTop: 8,
    },
    columnTitle: {
        fontFamily: FONT_OSWALD_MEDIUM,
        fontSize: 11,
        color: GOLD,
        letterSpacing: 2,
        marginBottom: 12,
        textTransform: "uppercase",
    },
    italicCaption: {
        fontFamily: FONT_LATO,
        fontSize: 9,
        color: GRAY,
        fontStyle: "italic",
        marginTop: 8,
        marginBottom: 28,
    },
    projSubtitle: {
        fontFamily: FONT_OSWALD,
        fontSize: 13,
        color: GRAY,
        marginBottom: 18,
    },
    footerBar: {
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: GOLD,
        paddingVertical: 10,
        paddingHorizontal: 24,
    },
    footerTagline: {
        fontFamily: FONT_LATO_BOLD,
        fontSize: 9,
        color: BLACK,
        textAlign: "center",
        marginBottom: 3,
    },
    footerProofRow: {
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        flexWrap: "wrap",
    },
    footerProofText: {
        fontFamily: FONT_LATO,
        fontSize: 9,
        color: BLACK,
        textAlign: "center",
    },
    footerProofSep: {
        fontFamily: FONT_LATO,
        fontSize: 9,
        color: BLACK,
        marginHorizontal: 6,
    },
    footerCtaLink: {
        fontFamily: FONT_LATO_BOLD,
        fontSize: 9,
        color: BLACK,
        textDecoration: "underline",
    },
});

// --- Primitives ---------------------------------------------------------

export const PageHeader = () => (
    <View fixed>
        <View style={styles.logoWrap}>
            <Image src={tdcLogoWhite} style={styles.logo} />
        </View>
        <View style={styles.headerRule} />
    </View>
);

export const FooterBar = () => (
    <View style={styles.footerBar} fixed>
        <Text style={styles.footerTagline}>{FOOTER_TAGLINE}</Text>
        <View style={styles.footerProofRow}>
            <Text style={styles.footerProofText}>{FOOTER_PROOF}</Text>
            <Text style={styles.footerProofSep}>|</Text>
            <Link src={FOOTER_CTA_HREF} style={styles.footerCtaLink}>
                {FOOTER_CTA_LABEL}
            </Link>
        </View>
    </View>
);

export const GoldBanner = ({ children }: { children: string }) => (
    <View style={styles.goldBanner}>
        <Text style={styles.goldBannerText}>{children}</Text>
    </View>
);

export const Bullet = ({ text }: { text: string }) => (
    <View style={styles.bulletRow}>
        <Text style={styles.bulletDot}>•</Text>
        <Text style={styles.bulletText}>{text}</Text>
    </View>
);

interface ScorePillProps {
    value: number;
    max?: number;
    label: string;
    isPercent?: boolean;
}
const ScoreDisplay = ({ value, max, label, isPercent }: ScorePillProps) => (
    <View style={styles.scoreCol}>
        {isPercent ? (
            <Text style={styles.scoreNumberPercent}>{value}%</Text>
        ) : (
            <Text style={styles.scoreNumber}>
                {value}
                <Text style={styles.scoreSlash}>/{max}</Text>
            </Text>
        )}
        <Text style={styles.scoreLabel}>{label}</Text>
    </View>
);

interface RadarDatum {
    label: string;
    value: number;
    max: number;
}

const RadarChart = ({
    data,
    size = 380,
}: {
    data: RadarDatum[];
    size?: number;
}) => {
    const cx = size / 2;
    const cy = size / 2;
    // Pull radius in so the labels around the rim have room to breathe.
    const R = size * 0.28;
    const n = data.length;
    const step = (2 * Math.PI) / n;
    const off = -Math.PI / 2;
    const pt = (i: number, v: number, mx: number = 100) => {
        const a = off + i * step;
        const r = (v / mx) * R;
        return [cx + r * Math.cos(a), cy + r * Math.sin(a)] as const;
    };
    const rings = [0.2, 0.4, 0.6, 0.8, 1.0];

    const ringPolyPoints = (pct: number) =>
        data.map((_, i) => pt(i, pct * 100).join(",")).join(" ");
    const valuePolyPoints = data
        .map((d, i) => pt(i, d.value, d.max).join(","))
        .join(" ");

    return (
        <Svg
            viewBox={`0 0 ${size} ${size}`}
            style={{ width: size, height: size }}
        >
            {rings.map((pct, ri) => (
                <Polygon
                    key={ri}
                    points={ringPolyPoints(pct)}
                    fill="none"
                    stroke={GOLD}
                    strokeWidth={0.6}
                    strokeOpacity={ri === rings.length - 1 ? 0.35 : 0.12}
                    strokeDasharray={ri < rings.length - 1 ? "3,3" : undefined}
                />
            ))}
            {data.map((_, i) => {
                const [ex, ey] = pt(i, 100);
                return (
                    <SvgLine
                        key={i}
                        x1={cx}
                        y1={cy}
                        x2={ex}
                        y2={ey}
                        stroke={GOLD}
                        strokeWidth={0.4}
                        strokeOpacity={0.15}
                    />
                );
            })}
            <Polygon
                points={valuePolyPoints}
                fill={GOLD}
                fillOpacity={0.15}
                stroke={GOLD}
                strokeWidth={2}
            />
            {data.map((d, i) => {
                const [px, py] = pt(i, d.value, d.max);
                return <Circle key={i} cx={px} cy={py} r={3} fill={GOLD} />;
            })}
            {data.map((d, i) => {
                // Place numeric value just outside each data point along the
                // radial direction so it stays clear of the polygon fill.
                const a = off + i * step;
                const r = (d.value / d.max) * R + 11;
                const vx = cx + r * Math.cos(a);
                const vy = cy + r * Math.sin(a) + 2.5;
                return (
                    <SvgText
                        key={`v-${i}`}
                        x={vx}
                        y={vy}
                        fill={WHITE}
                        textAnchor="middle"
                        style={{ fontSize: 8, fontFamily: FONT_LATO_BOLD }}
                    >
                        {String(d.value)}
                    </SvgText>
                );
            })}
            {data.flatMap((d, i) => {
                const a = off + i * step;
                const lr = R + 30;
                const lx = cx + lr * Math.cos(a);
                const ly = cy + lr * Math.sin(a);
                const lines = wrapLabel(d.label, 14);
                const lineHeight = 10;
                const startY = ly - ((lines.length - 1) * lineHeight) / 2;
                return lines.map((line, li) => (
                    <SvgText
                        key={`${i}-${li}`}
                        x={lx}
                        y={startY + li * lineHeight}
                        fill={GRAY_LIGHT}
                        textAnchor="middle"
                        style={{ fontSize: 8.5, fontFamily: FONT_LATO }}
                    >
                        {line}
                    </SvgText>
                ));
            })}
        </Svg>
    );
};

function wrapLabel(label: string, max: number): string[] {
    if (label.length <= max) return [label];
    const words = label.split(/\s+/);
    const lines: string[] = [];
    let buf = "";
    for (const w of words) {
        if ((buf + " " + w).trim().length > max) {
            if (buf) lines.push(buf);
            buf = w;
        } else {
            buf = (buf + " " + w).trim();
        }
    }
    if (buf) lines.push(buf);
    return lines;
}

// --- Tables -------------------------------------------------------------

interface BreakdownRow {
    section: string;
    score: string;
    strengths: string;
    gaps: string;
}

const BreakdownTable = ({
    columns,
    rows,
}: {
    columns: string[];
    rows: BreakdownRow[];
}) => (
    <View style={styles.table}>
        <View style={styles.tableHeaderRow} fixed>
            <Text style={[styles.tableHeaderCell, styles.colSection]}>
                {columns[0]}
            </Text>
            <Text style={[styles.tableHeaderCell, styles.colScore]}>
                {columns[1]}
            </Text>
            <Text style={[styles.tableHeaderCell, styles.colStrengths]}>
                {columns[2]}
            </Text>
            <Text style={[styles.tableHeaderCell, styles.colGaps]}>
                {columns[3]}
            </Text>
        </View>
        {rows.map((r, i) => (
            <View
                key={r.section}
                style={i % 2 === 0 ? styles.tableRow : styles.tableRowAlt}
                wrap={false}
            >
                <Text style={[styles.cellSection, styles.colSection]}>
                    {r.section}
                </Text>
                <Text style={[styles.cellBody, styles.colScore]}>
                    {r.score}
                </Text>
                <Text style={[styles.cellBody, styles.colStrengths]}>
                    {r.strengths}
                </Text>
                <Text style={[styles.cellBody, styles.colGaps]}>
                    {r.gaps}
                </Text>
            </View>
        ))}
    </View>
);

interface ProjectionRow {
    category: string;
    current: string;
    projected: string;
    lift: string;
}

const ProjectionTable = ({ rows }: { rows: ProjectionRow[] }) => (
    <View style={styles.table}>
        <View style={styles.tableHeaderRow} fixed>
            <Text style={[styles.tableHeaderCell, styles.projCategory]}>
                Category
            </Text>
            <Text style={[styles.tableHeaderCell, styles.projCol]}>
                Current Score
            </Text>
            <Text style={[styles.tableHeaderCell, styles.projCol]}>
                With TDC Content
            </Text>
            <Text style={[styles.tableHeaderCell, styles.projCol]}>
                Projected Lift
            </Text>
        </View>
        {rows.map((r, i) => (
            <View
                key={r.category}
                style={i % 2 === 0 ? styles.tableRow : styles.tableRowAlt}
                wrap={false}
            >
                <Text style={[styles.cellSection, styles.projCategory]}>
                    {r.category}
                </Text>
                <Text style={[styles.cellBody, styles.projCol]}>
                    {r.current}
                </Text>
                <Text style={[styles.cellBody, styles.projCol]}>
                    {r.projected}
                </Text>
                <Text style={[styles.cellLift, styles.projCol]}>
                    {r.lift}
                </Text>
            </View>
        ))}
    </View>
);

interface LiftRow {
    section: string;
    current: number;
    projected: number;
    driver: string;
}

const LiftTable = ({ rows }: { rows: LiftRow[] }) => (
    <View style={styles.table}>
        <View style={styles.tableHeaderRow} fixed>
            <Text style={[styles.tableHeaderCellSmall, styles.liftSection]}>
                Section
            </Text>
            <Text style={[styles.tableHeaderCellSmall, styles.liftScore]}>
                Cur
            </Text>
            <Text style={[styles.tableHeaderCellSmall, styles.liftScore]}>
                Proj
            </Text>
            <Text style={[styles.tableHeaderCellSmall, styles.liftDriver]}>
                Driver
            </Text>
        </View>
        {rows.map((r, i) => (
            <View
                key={r.section}
                style={i % 2 === 0 ? styles.tableRow : styles.tableRowAlt}
                wrap={false}
            >
                <Text style={[styles.cellSectionSmall, styles.liftSection]}>
                    {r.section}
                </Text>
                <Text style={[styles.cellBodySmall, styles.liftScore]}>
                    {r.current}
                </Text>
                <Text style={[styles.cellBodySmall, styles.liftScore]}>
                    {r.projected}
                </Text>
                <Text style={[styles.cellBodySmall, styles.liftDriver]}>
                    {r.driver}
                </Text>
            </View>
        ))}
    </View>
);

// --- Pages --------------------------------------------------------------

interface Props {
    data: PdpAuditResponse;
}

export function PdpAuditPdf({ data }: Props) {
    const { audit, meta } = data;
    return (
        <Document>
            <OverviewPage audit={audit} url={meta.url} />
            <HumanConversionPage audit={audit} />
            <AgenticCommercePage audit={audit} />
            <ProjectedPage audit={audit} />
        </Document>
    );
}

function OverviewPage({ audit, url }: { audit: PdpAudit; url: string }) {
    return (
        <Page size="LETTER" style={styles.page}>
            <PageHeader />

            <View style={styles.h1Wrap}>
                <Text style={styles.h1Display}>Product Page</Text>
                <Text style={styles.h1Accent}>Audit</Text>
            </View>

            <View style={styles.heroPanel}>
                <View>
                    <Text style={styles.heroBrand}>
                        {audit.brandName || "Unknown Brand"}
                    </Text>
                    <Text style={styles.heroProduct}>
                        {audit.productName || "Unknown Product"}
                    </Text>
                    <Text style={styles.heroLabel}>PDP Audit</Text>
                    <Text style={styles.heroUrl}>{url}</Text>
                </View>
            </View>

            <View style={styles.overallBadge}>
                <Text style={styles.overallBadgeText}>
                    Overall Score:{" "}
                    <Text style={styles.overallBadgePct}>
                        {audit.scores.overall}%
                    </Text>
                </Text>
            </View>

            <View style={styles.breakdownPanel}>
                <Text style={styles.breakdownTitle}>Score Breakdown</Text>
                <View style={styles.breakdownRule} />
                <View style={styles.scoreRow}>
                    <ScoreDisplay
                        value={audit.scores.humanConversion}
                        max={100}
                        label="Human Conversion Readiness"
                    />
                    <ScoreDisplay
                        value={audit.scores.agenticCommerce}
                        max={100}
                        label="Agentic Commerce Readiness"
                    />
                    <ScoreDisplay
                        value={audit.scores.overall}
                        label="Overall PDP Effectiveness"
                        isPercent
                    />
                </View>
            </View>

            <View break>
                <Text style={styles.sectionH2}>Key Findings</Text>
                <View style={styles.findingsRow}>
                    <View style={styles.findingsCard} wrap={false}>
                        <Text style={styles.findingsTitle}>Strengths</Text>
                        {audit.keyFindings.strengths.map((s, i) => (
                            <Bullet key={i} text={s} />
                        ))}
                    </View>
                    <View style={styles.findingsCard} wrap={false}>
                        <Text style={styles.findingsTitle}>Opportunities</Text>
                        {audit.keyFindings.opportunities.map((o, i) => (
                            <Bullet key={i} text={o} />
                        ))}
                    </View>
                </View>
            </View>

            <FooterBar />
        </Page>
    );
}

function sectionToScoreOutOf100(section: { score: string }): number {
    const { current, max } = parseScore(section.score);
    if (!max) return 0;
    return Math.round((current / max) * 100);
}

function HumanConversionPage({ audit }: { audit: PdpAudit }) {
    const radarData: RadarDatum[] = HUMAN_CONVERSION_SECTION_LABELS.map(
        ({ key, label }) => ({
            label,
            value: sectionToScoreOutOf100(audit.humanConversion[key]),
            max: 100,
        }),
    );
    const rows: BreakdownRow[] = HUMAN_CONVERSION_SECTION_LABELS.map(
        ({ key, label }) => ({
            section: label,
            score: audit.humanConversion[key].score,
            strengths: audit.humanConversion[key].strengths,
            gaps: audit.humanConversion[key].gaps,
        }),
    );

    return (
        <Page size="LETTER" style={styles.page}>
            <PageHeader />
            <GoldBanner>
                {`Human Conversion Readiness: ${audit.scores.humanConversion}/100`}
            </GoldBanner>
            <View style={styles.radarWrap}>
                <RadarChart data={radarData} size={380} />
            </View>
            <Text style={styles.sectionH2}>Score Breakdown</Text>
            <BreakdownTable
                columns={["Section", "Score", "Strengths", "Gaps"]}
                rows={rows}
            />
            <FooterBar />
        </Page>
    );
}

function AgenticCommercePage({ audit }: { audit: PdpAudit }) {
    const radarData: RadarDatum[] = AGENTIC_COMMERCE_DIMENSION_LABELS.map(
        ({ key, label }) => ({
            label,
            value: sectionToScoreOutOf100(audit.agenticCommerce[key]),
            max: 100,
        }),
    );
    const rows: BreakdownRow[] = AGENTIC_COMMERCE_DIMENSION_LABELS.map(
        ({ key, label }) => ({
            section: label,
            score: audit.agenticCommerce[key].score,
            strengths: audit.agenticCommerce[key].strengths,
            gaps: audit.agenticCommerce[key].gaps,
        }),
    );

    return (
        <Page size="LETTER" style={styles.page}>
            <PageHeader />
            <GoldBanner>
                {`Agentic Commerce Readiness: ${audit.scores.agenticCommerce}/100`}
            </GoldBanner>
            <View style={styles.radarWrap}>
                <RadarChart data={radarData} size={380} />
            </View>
            <Text style={styles.sectionH2}>Score Breakdown</Text>
            <BreakdownTable
                columns={["Dimension", "Score", "Strengths", "Gaps"]}
                rows={rows}
            />
            <FooterBar />
        </Page>
    );
}

function ProjectedPage({ audit }: { audit: PdpAudit }) {
    const human = projectHumanConversion(audit);
    const agentic = projectAgenticCommerce(audit);
    const overall = projectOverall(audit);

    const projectedMain: ProjectionRow[] = [
        {
            category: "Human Conversion Readiness",
            current: String(audit.scores.humanConversion),
            projected: String(human.projectedTotal),
            lift: `+${Math.max(0, human.projectedTotal - audit.scores.humanConversion)}`,
        },
        {
            category: "Agentic Commerce Readiness",
            current: String(audit.scores.agenticCommerce),
            projected: String(agentic.projectedTotal),
            lift: `+${Math.max(0, agentic.projectedTotal - audit.scores.agenticCommerce)}`,
        },
        {
            category: "Overall PDP Effectiveness",
            current: `${overall.current}%`,
            projected: `${overall.projected}%`,
            lift: `+${Math.max(0, overall.projected - overall.current)}%`,
        },
    ];

    const humanLift: LiftRow[] = human.rows.map((r) => ({
        section: prettyKey(r.label),
        current: r.current,
        projected: r.projected,
        driver: r.driver,
    }));
    const agenticLift: LiftRow[] = agentic.rows.map((r) => ({
        section: prettyKey(r.label),
        current: r.current,
        projected: r.projected,
        driver: r.driver,
    }));

    return (
        <Page size="LETTER" style={styles.page}>
            <PageHeader />
            <GoldBanner>
                Projected Performance with Expert-Led Product Content
            </GoldBanner>
            <Text style={styles.projSubtitle}>
                Before vs After Score Comparison (Best-in-class PDPs: 90+ · 80+ is considered good)
            </Text>
            <Text style={styles.italicCaption}>
                Lift applies whether expert content is newly produced or already
                exists on the page but isn't yet discoverable by AI agents — if
                your PDP already has expert-led video or content that isn't
                surfaced in structured data, projected lift represents the value
                of making it machine-readable.
            </Text>
            <ProjectionTable rows={projectedMain} />
            <Text style={styles.italicCaption}>
                Technical Performance and Mobile-First remain unchanged and
                are included in the projected total.
            </Text>
            <Text style={styles.sectionH2}>Category Lift Projection</Text>
            <Text style={styles.projSubtitle}>
                Best-in-class PDPs: 90+ · 80+ is considered good
            </Text>
            <Text style={styles.italicCaption}>
                Lift applies whether expert content is newly produced or already
                exists on the page but isn't yet discoverable by AI agents — if
                your PDP already has expert-led video or content that isn't
                surfaced in structured data, projected lift represents the value
                of making it machine-readable.
            </Text>
            <View style={styles.twoCol}>
                <View style={{ flex: 1 }}>
                    <Text style={styles.columnTitle}>
                        Human PDP Experience
                    </Text>
                    <LiftTable rows={humanLift} />
                </View>
                <View style={{ flex: 1 }}>
                    <Text style={styles.columnTitle}>
                        Agentic PDP Experience
                    </Text>
                    <LiftTable rows={agenticLift} />
                </View>
            </View>
            <FooterBar />
        </Page>
    );
}

function prettyKey(camel: string): string {
    return camel
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (c) => c.toUpperCase())
        .trim();
}
